import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  AccountBody,
  BulkAction,
  EntryBody,
  EntrySuggestionBody,
  TransferBody,
  TransferMutationPreviewRequest,
} from '@saldo/contracts';

import { useAuthenticatedSession } from '../session/SessionBoundary';
import { BUSINESS_TIME_ZONE, businessMonth, monthBounds, monthLabel, shiftMonth } from './calendar';
import { hasRowFilters, type LedgerContext } from './context';
import {
  commitBulkMutation,
  commitEntryLifecycle,
  commitMutation,
  commitTransferMutation,
  createAccount,
  createBulkPreview,
  createCategory,
  createEntry,
  createEntryLifecyclePreview,
  createMutationPreview,
  createTransferPreview,
  deleteAccount,
  deleteCategory,
  describeLedgerFailure,
  endsSession,
  LedgerRequestError,
  listAccounts,
  listCategories,
  listEntries,
  listEntrySuggestions,
  listTransferPairSuggestions,
  listTransfers,
  patchAccount,
  readInstallmentGroup,
  setEntryCancellation,
  updateCategory,
  updateEntry,
} from './client';
import { AccountEditor, EntryEditor, type LifecycleState } from './EntryEditor';
import { BulkActions, type BulkState } from './BulkActions';
import {
  bulkPreviewView,
  clearSelection as clearBulkSelection,
  commitBlocker,
  describeBulkRefusals,
  isSelected,
  toBulkRequest,
  toggleSelection,
  type BulkOptions,
  type BulkSelection,
} from './bulk';
import { CategoryPanel } from './CategoryPanel';
import { PaymentPanel, type PaymentMode } from './PaymentPanel';
import { toAccountCreate, toEntryCreate, toEntryPatch, toPayPatch, type AccountForm, type EntryForm } from './forms';
import {
  categoryChoiceFrom,
  describeReferenceRefusal,
  toCategoryArchive,
  toCategoryCreate,
  toCategoryRename,
  type CategoryTarget,
} from './categories';
import {
  lifecyclePreviewView,
  reuseSuggestion,
  toEntryDelete,
  toEntryDuplicate,
} from './lifecycle';
import {
  editFieldsFrom,
  groupView,
  parseInstallmentCount,
  previewView,
  toInstallmentsCreate,
  toInstallmentsDelete,
  toInstallmentsEdit,
  type InstallmentGroupView,
  type InstallmentPreviewView,
  type InstallmentScope,
} from './installments';
import {
  applySuggestion,
  pairSuggestionView,
  paymentPreviewView,
  paymentRows,
  toPaymentComplete,
  toPaymentDelete,
  toPaymentRequest,
  TRANSFER_PAGE_LIMIT,
  type PairSuggestionView,
  type PaymentForm,
  type PaymentPreviewView,
} from './payments';
import { formatBalance, formatMoney, formatMoneyMagnitude } from './money';
import {
  classifyWrite,
  describeSaveState,
  intentSignature,
  keyForIntent,
  newOperationKey,
  stateAfterWrite,
  type PendingIntent,
  type SaveState,
} from './save';
import {
  byCurrency,
  cardState,
  chronologicalRows,
  ENTRY_PAGE_LIMIT,
  loadedHistory,
  matchingActivity,
  selectRows,
  summarise,
  type CardState,
} from './view';

/**
 * The signed-in workspace: the ledger screen, and the accounts screen (P4-T2; the shell's own
 * navigation added at C4a).
 *
 * It renders **one screen** of the app, chosen by the shell's address:
 *
 *   - `screen="ledger"` — the account directory as the left column, the monthly ledger as the
 *     right one, and the entry, payment and bulk surfaces on top of them.
 *   - `screen="accounts"` — the same directory, with the categories panel beside it, for the
 *     purposes that are setup rather than the month's money.
 *
 * The two are one component because they are the same two reads (`listAccounts`, `listCategories`)
 * and the same four-state write reporting: two components would be two copies of the write path,
 * which is how a save that says "saved" on one screen comes to say nothing on the other.
 *
 * Structure worth knowing before reading on:
 *
 *   - **The address belongs to the shell.** Account, month, mode, search, filters, sort and page
 *     live in the query string, and the shell reads it and writes it — this component receives its
 *     `context` as a prop and asks for a change through `navigate`. It touches neither `window` nor
 *     `history`, which is what keeps two components from ever writing the same address.
 *   - **Nothing money-shaped is decided in JSX.** Every figure is a `view.ts` value, which is
 *     in turn `@saldo/domain`'s arithmetic — there is one eligibility rule and it lives in the
 *     domain package.
 *   - **The write path is explicit about what it does not know.** A save reports `saved`,
 *     `saved but not refreshed`, `not saved`, or `unknown`, and an unknown outcome offers
 *     reconciliation of the same intent (same `Idempotency-Key`) — never a fresh create.
 */

/** The screens this workspace renders. The other two are their own components. */
export type WorkspaceScreen = 'ledger' | 'accounts';

export interface LedgerWorkspaceProps {
  /** Which of the two screens this instance is. */
  readonly screen: WorkspaceScreen;
  /** The ledger's context, read from the address by the shell. */
  readonly context: LedgerContext;
  /** Ask the shell to move the address to a changed context. */
  readonly navigate: (patch: Partial<LedgerContext>, options?: { readonly replace?: boolean }) => void;
}

interface EditorState {
  /** `null` means "creating a new entry". */
  readonly entry: EntryBody | null;
}

/**
 * What the payment panel is open on: a whole pair and which of the three whole-pair operations
 * it is performing. `transfer` is `null` only for a creation.
 */
interface PaymentEditorState {
  readonly mode: PaymentMode;
  readonly transfer: TransferBody | null;
}

const WRITE_MESSAGES: Partial<Record<SaveState, string>> = {
  saving: 'Saving…',
  saved: 'Saved.',
  'saved-refresh-failed':
    'Saved, but this screen could not be refreshed — the entry is stored; reload to see it.',
  'not-saved': 'Not saved. The API refused this, so nothing was stored.',
  unknown: 'No answer from the API — it may or may not have been stored. Reconcile before retrying as new.',
};

/**
 * What a card figure's word says, in full, beside the figure (`CARD-03`).
 *
 * A card balance is not bank cash: negative is debt, positive is credit, and this is where
 * the two are told apart rather than one being labelled as the other.
 */
const CARD_STATE_TEXT: Record<CardState, string> = {
  debt: 'Debt — the signed card figure is negative: this is what the card owes, and it is not bank cash.',
  credit: 'Credit — the signed card figure is positive: this is credit in your favour, not money owed.',
  settled: 'Settled — the signed card figure is zero: neither debt nor credit.',
};

export function LedgerWorkspace({ screen, context, navigate }: LedgerWorkspaceProps) {
  const session = useAuthenticatedSession();
  const fallbackMonth = businessMonth(BUSINESS_TIME_ZONE, new Date());

  const accountsQuery = useQuery({
    queryKey: ['ledger', 'accounts'],
    queryFn: ({ signal }) => listAccounts(session.csrfToken, signal),
  });

  const accountId = context.account;
  const entriesQuery = useQuery({
    queryKey: ['ledger', 'entries', accountId, context.canceled],
    enabled: accountId !== null,
    queryFn: ({ signal }) =>
      listEntries(session.csrfToken, accountId as string, {
        limit: ENTRY_PAGE_LIMIT,
        // Canceled rows are asked for only when the view asks for them: the ordinary ledger is the
        // live one, and a canceled row is a record to inspect rather than a row to read by default.
        includeCanceled: context.canceled,
        signal,
      }),
  });

  /**
   * The categories, archived ones included.
   *
   * It is a read of the same owner's data and it is refetched with every accepted write, because a
   * category's totals are a derivation over the entries: a write that changes an entry changes what
   * the panel shows.
   */
  const categoriesQuery = useQuery({
    queryKey: ['ledger', 'categories'],
    queryFn: ({ signal }) => listCategories(session.csrfToken, signal),
  });

  // The account's payment pairs (`GET /api/v1/transfers?accountId=`), which is what the payment
  // panel reviews before it completes or cancels one: a pair has no leg-level edit, so the
  // pair's own read is the only honest basis for its revision.
  const transfersQuery = useQuery({
    queryKey: ['ledger', 'transfers', accountId],
    enabled: accountId !== null,
    queryFn: ({ signal }) =>
      listTransfers(session.csrfToken, { accountId: accountId as string, limit: TRANSFER_PAGE_LIMIT, signal }),
  });

  // The account pairs the owner has already used (`GET /api/v1/transfer-pair-suggestions`), which
  // the payment form offers for **explicit** reuse (TRF-06). It is a read of the owner's own
  // history: nothing it returns is applied without the user choosing it.
  const pairSuggestionsQuery = useQuery({
    queryKey: ['ledger', 'transfer-pair-suggestions'],
    enabled: accountId !== null,
    queryFn: ({ signal }) => listTransferPairSuggestions(session.csrfToken, { limit: 10, signal }),
  });

  // A 401 is the boundary's business: report it and let the gate take over.
  const accountsError = accountsQuery.isError ? (accountsQuery.error as unknown) : null;
  const entriesError = entriesQuery.isError ? (entriesQuery.error as unknown) : null;
  const transfersError = transfersQuery.isError ? (transfersQuery.error as unknown) : null;
  useEffect(() => {
    for (const error of [accountsError, entriesError, transfersError]) {
      if (error instanceof LedgerRequestError && endsSession(error.kind)) session.reportSessionLost();
    }
  }, [accountsError, entriesError, transfersError, session]);

  const accounts = useMemo(() => accountsQuery.data?.accounts ?? [], [accountsQuery.data]);
  const account = useMemo(
    () => accounts.find((candidate) => candidate.id === accountId) ?? null,
    [accounts, accountId],
  );

  // With accounts but no account in the address, open the first one rather than an empty view.
  //
  // Only on the ledger screen: the accounts screen is not showing a month of one account, so it
  // has no empty view to avoid — and its own rows are what move the address, to the ledger with the
  // account clicked.
  useEffect(() => {
    if (screen !== 'ledger') return;
    if (accounts.length === 0) return;
    if (accountId !== null && accounts.some((candidate) => candidate.id === accountId)) return;
    navigate({ account: accounts[0]?.id ?? null, page: 1 }, { replace: true });
  }, [accounts, accountId, navigate, screen]);

  const businessToday = accountsQuery.data?.meta.businessToday ?? entriesQuery.data?.meta.businessToday ?? null;
  const entries = useMemo(() => entriesQuery.data?.entries ?? [], [entriesQuery.data]);
  const transfers = useMemo(() => transfersQuery.data?.transfers ?? [], [transfersQuery.data]);
  const paymentSuggestions: PairSuggestionView[] = useMemo(
    () => (pairSuggestionsQuery.data?.suggestions ?? []).map((suggestion) => pairSuggestionView(suggestion)),
    [pairSuggestionsQuery.data],
  );
  const history = useMemo(() => loadedHistory(entries, ENTRY_PAGE_LIMIT), [entries]);
  const bounds = useMemo(() => monthBounds(context.month), [context.month]);

  const summary = useMemo(
    () =>
      account === null || businessToday === null
        ? null
        : summarise({
            account,
            entries,
            monthStart: bounds.start,
            monthEndExclusive: bounds.endExclusive,
            businessToday,
            history,
          }),
    [account, entries, bounds, businessToday, history],
  );

  const selection = useMemo(() => {
    if (account === null || businessToday === null) return null;
    return selectRows(chronologicalRows(entries, account.kind, businessToday), {
      mode: context.mode,
      query: context.query,
      type: context.type,
      state: context.state,
      sort: context.sort,
      monthStart: bounds.start,
      monthEndExclusive: bounds.endExclusive,
      businessToday,
      page: context.page,
    });
  }, [account, entries, businessToday, bounds, context.mode, context.query, context.type, context.state, context.sort, context.page]);

  const activity = useMemo(() => {
    if (selection === null || account === null) return null;
    return matchingActivity(selection, account.kind, bounds.start, bounds.endExclusive);
  }, [selection, account, bounds]);

  const groups = useMemo(() => byCurrency(accounts), [accounts]);

  /* ------------------------------------------------------------------ writing --- */

  const [editor, setEditor] = useState<EditorState | null>(null);
  const [accountEditorOpen, setAccountEditorOpen] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [saveMessage, setSaveMessage] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [operationKey, setOperationKey] = useState<{ key: string; reused: boolean } | null>(null);
  const pendingRef = useRef<PendingIntent | null>(null);

  /* ----- installments (P5-T2): the preview the user reads, and the commit they confirm ----- */

  const [installmentCount, setInstallmentCount] = useState('1');
  const [installmentScope, setInstallmentScope] = useState<InstallmentScope>('single');
  const [installmentGroup, setInstallmentGroup] = useState<InstallmentGroupView | null>(null);
  const [preview, setPreview] = useState<InstallmentPreviewView | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [installmentBusy, setInstallmentBusy] = useState(false);

  function resetInstallments(): void {
    setInstallmentCount('1');
    setInstallmentScope('single');
    setInstallmentGroup(null);
    setPreview(null);
    setPreviewError(null);
    setInstallmentBusy(false);
  }

  /* ----- payments (P6-T2): the pair, reviewed on both accounts before it is committed ----- */

  const [payment, setPayment] = useState<PaymentEditorState | null>(null);
  const [paymentPreview, setPaymentPreview] = useState<PaymentPreviewView | null>(null);
  const [paymentPreviewError, setPaymentPreviewError] = useState<string | null>(null);
  const [paymentBusy, setPaymentBusy] = useState(false);
  // The last preview intent and the key it carried, so a reconciliation can re-send the *same*
  // intent rather than a lookalike one.
  const paymentIntentRef = useRef<{ request: TransferMutationPreviewRequest; key: string } | null>(null);

  function resetPayments(): void {
    setPayment(null);
    setPaymentPreview(null);
    setPaymentPreviewError(null);
    setPaymentBusy(false);
    paymentIntentRef.current = null;
  }

  /* ----- explicit-ID bulk (P8-T3): the checked ids, and the preview they are bound to ----- */

  /**
   * The checked rows, as exact ids.
   *
   * Component state rather than the URL, and deliberately not derived from the filter: a page of
   * matching rows is not a selection (`BAL-07`). It survives paging, because paging rewrites the
   * address while this list is untouched.
   */
  const [bulkSelection, setBulkSelection] = useState<BulkSelection>(clearBulkSelection);
  const [bulk, setBulk] = useState<BulkState>({ action: null, preview: null, busy: false, error: null });

  /** Check or uncheck one row. An open preview is *kept*, so the staleness is visible, not silent. */
  function toggleBulkRow(entryId: string): void {
    setBulkSelection((current) => toggleSelection(current, entryId));
  }

  function clearBulk(): void {
    setBulkSelection(clearBulkSelection);
    setBulk({ action: null, preview: null, busy: false, error: null });
  }

  /**
   * Store a preview of one bulk action over the exact checked ids.
   *
   * A bulk preview moves no money, so its failure is a local error; the four save states belong to
   * the commit. The API's own per-id refusals (`422 BULK_ROW_NOT_ELIGIBLE`) are rendered as the
   * named rows they are rather than as a generic refusal, because "which id" is the whole point of
   * an exact-set action (`F-30`).
   */
  async function takeBulkPreview(action: BulkAction, options: BulkOptions): Promise<void> {
    const mapped = toBulkRequest(action, bulkSelection, options);
    if (!mapped.ok) {
      setBulk({ action, preview: null, busy: false, error: mapped.error });
      return;
    }
    setBulk({ action, preview: null, busy: true, error: null });
    try {
      const stored = await createBulkPreview(session.csrfToken, mapped.body, {
        idempotencyKey: newOperationKey(),
      });
      setBulk({
        action,
        preview: bulkPreviewView(stored, categoriesQuery.data?.categories ?? []),
        busy: false,
        error: null,
      });
    } catch (error) {
      if (error instanceof LedgerRequestError && endsSession(error.kind)) session.reportSessionLost();
      const named = error instanceof LedgerRequestError ? describeBulkRefusals(error.fieldErrors) : null;
      setBulk({
        action,
        preview: null,
        busy: false,
        error:
          named ??
          (error instanceof LedgerRequestError
            ? describeLedgerFailure(error.kind)
            : 'The preview could not be taken.'),
      });
    }
  }

  /**
   * Commit the stored preview.
   *
   * The commit names the preview's own id and nothing else, so the set that was reviewed is the set
   * that is written. The two client-visible moves are refused before the write (`commitBlocker`) and
   * the third — a row's own moved version — comes back as the API's `409 STALE_PREVIEW`.
   */
  async function confirmBulk(): Promise<void> {
    const stored = bulk.preview;
    if (stored === null) return;
    const blocker = commitBlocker(stored, bulkSelection, businessToday ?? fallbackMonth);
    if (blocker !== null) {
      setBulk((current) => ({ ...current, error: blocker }));
      return;
    }
    const { state } = await commit({
      operation: 'bulk.commit',
      targetId: stored.id,
      body: { previewId: stored.id, confirm: true },
      send: (key) => commitBulkMutation(session.csrfToken, stored.id, { idempotencyKey: key }),
      refusal: (error) =>
        error.code === 'STALE_PREVIEW'
          ? 'The rows changed since this preview. Take a new preview.'
          : describeLedgerFailure(error.kind),
    });
    if (state === 'saved') {
      setBulkSelection(clearBulkSelection);
      setBulk({ action: null, preview: null, busy: false, error: null });
    }
  }

  function resetSave(): void {
    pendingRef.current = null;
    setSaveState('idle');
    setSaveMessage('');
    setSaveError(null);
    setOperationKey(null);
    resetInstallments();
    resetPayments();
  }

  /* ----- categories (P7-T1): the label surface and its named refusals ----- */

  /**
   * Whether archived categories are shown.
   *
   * A view preference rather than a query: the read returns archived categories either way,
   * because archiving is visibility and the surface is what decides to hide them. That is also why
   * this is component state and not the URL — it changes nothing the server is asked for.
   */
  const [showArchivedCategories, setShowArchivedCategories] = useState(false);
  /** The same preference for the accounts directory, whose read also carries archived rows. */
  const [showArchivedAccounts, setShowArchivedAccounts] = useState(false);

  /* ----- the entry lifecycle (P7-T1): the stored impact, and what the user acknowledged ----- */

  const [lifecycle, setLifecycle] = useState<LifecycleState>({
    preview: null,
    error: null,
    busy: false,
    acknowledged: false,
  });
  /** The autocomplete substring; the read is scoped to the open editor. */
  const [suggestionQuery, setSuggestionQuery] = useState('');

  function resetLifecycle(): void {
    setLifecycle({ preview: null, error: null, busy: false, acknowledged: false });
    setSuggestionQuery('');
  }

  /**
   * Perform one write, and report honestly what is known about it.
   *
   * The operation key is derived from the intent: an identical intent keeps the key it
   * already had (so a retry is the same operation to the API), and anything else mints a new
   * one. The refresh is part of the write, because "saved" and "this screen shows the saved
   * state" are different claims.
   */
  async function commit(args: {
    operation: string;
    targetId: string | null;
    body: unknown;
    send: (operationKey: string) => Promise<unknown>;
    /**
     * How a refusal is worded, when the generic vocabulary is not enough.
     *
     * A reference-safe deletion's `409` names the reference that blocked it, and that sentence has
     * to reach the user — the refusal is the protection. Everything else keeps the shared
     * vocabulary, which is why this is a hook rather than a second message path.
     */
    refusal?: (error: LedgerRequestError) => string;
  }): Promise<{ state: SaveState; value: unknown }> {
    const signature = intentSignature(args.operation, args.targetId, args.body);
    const chosen = keyForIntent(pendingRef.current, signature);
    pendingRef.current = {
      operation: args.operation,
      targetId: args.targetId,
      body: args.body,
      idempotencyKey: chosen.key,
    };
    setOperationKey(chosen);
    setSaveError(null);
    setSaveState('saving');
    setSaveMessage(WRITE_MESSAGES.saving ?? describeSaveState('saving'));

    let value: unknown;
    try {
      value = await args.send(chosen.key);
    } catch (error) {
      const status = error instanceof LedgerRequestError ? error.status : null;
      const kind = classifyWrite(status);
      const state = stateAfterWrite(kind, true);
      if (error instanceof LedgerRequestError && endsSession(error.kind)) session.reportSessionLost();
      setSaveState(state);
      setSaveMessage(
        kind === 'unknown'
          ? (WRITE_MESSAGES.unknown ?? describeSaveState('unknown'))
          : error instanceof LedgerRequestError
            ? (args.refusal?.(error) ?? describeLedgerFailure(error.kind))
            : (WRITE_MESSAGES['not-saved'] ?? describeSaveState('not-saved')),
      );
      return { state, value: null };
    }

    let refreshOk = true;
    try {
      const [accountsResult, entriesResult, categoriesResult] = await Promise.all([
        accountsQuery.refetch(),
        entriesQuery.refetch(),
        categoriesQuery.refetch(),
      ]);
      if (accountsResult.isError) throw accountsResult.error;
      if (entriesResult.isError) throw entriesResult.error;
      if (categoriesResult.isError) throw categoriesResult.error;
      if (accountId !== null) {
        const transfersResult = await transfersQuery.refetch();
        if (transfersResult.isError) throw transfersResult.error;
      }
    } catch {
      refreshOk = false;
    }

    const state = stateAfterWrite('accepted', refreshOk);
    if (state === 'saved') pendingRef.current = null;
    setSaveState(state);
    setSaveMessage(WRITE_MESSAGES[state] ?? describeSaveState(state));
    return { state, value };
  }

  function openEntryEditor(entry: EntryBody | null): void {
    resetSave();
    resetLifecycle();
    setAccountEditorOpen(false);
    setEditor({ entry });
  }

  function closeEditor(): void {
    resetSave();
    resetLifecycle();
    setEditor(null);
  }

  /**
   * The autocomplete read (`GET /api/v1/entry-suggestions`).
   *
   * Enabled only while an entry editor is open and at least two characters have been typed: an
   * unfiltered read of every past description is not what an autocomplete is for, and the API's own
   * bound is one page. The failure is reported in place — a suggestion list that failed to load
   * must not read as "no matches".
   */
  const suggestionsQuery = useQuery({
    queryKey: ['ledger', 'suggestions', accountId, suggestionQuery],
    enabled: editor !== null && suggestionQuery.trim().length >= 2,
    queryFn: ({ signal }) =>
      listEntrySuggestions(session.csrfToken, {
        q: suggestionQuery.trim(),
        ...(accountId === null ? {} : { accountId }),
        signal,
      }),
  });

  // A member's editor needs its group: the revision a commit must state, and the scheduled count
  // that makes "of 12" truthful after a deletion.
  const memberGroupId = editor?.entry?.installmentGroupId ?? null;
  useEffect(() => {
    if (memberGroupId === null) {
      setInstallmentGroup(null);
      return;
    }
    let cancelled = false;
    readInstallmentGroup(session.csrfToken, memberGroupId)
      .then((read) => {
        if (!cancelled) setInstallmentGroup(groupView(read));
      })
      .catch(() => {
        // Without the group the editor cannot state a revision, so a commit is refused rather
        // than sent blind — the API's own `If-Match` requirement is what makes that safe.
        if (!cancelled) setInstallmentGroup(null);
      });
    return () => {
      cancelled = true;
    };
  }, [memberGroupId, session.csrfToken]);

  const accountRole = account?.kind ?? 'cash';

  /**
   * Store a preview of one installment change.
   *
   * The preview stores no money: it records the exact selection and the eligibility decided at the
   * business day, and the commit is what writes. So its failure is reported as the local error it
   * is — the four save states belong to the money write that follows — and the preview is the
   * *only* thing a commit can name, which is what makes a stale membership uncommittable.
   */
  async function takePreview(body: Parameters<typeof createMutationPreview>[1]): Promise<void> {
    setPreviewError(null);
    setSaveError(null);
    setInstallmentBusy(true);
    try {
      const stored = await createMutationPreview(session.csrfToken, body, {
        idempotencyKey: newOperationKey(),
      });
      setPreview(previewView(stored));
    } catch (error) {
      setPreview(null);
      if (error instanceof LedgerRequestError && endsSession(error.kind)) session.reportSessionLost();
      setPreviewError(
        error instanceof LedgerRequestError
          ? describeLedgerFailure(error.kind)
          : 'The preview could not be taken.',
      );
    } finally {
      setInstallmentBusy(false);
    }
  }

  /** Commit the stored preview, confirmed — the write that carries the four save states. */
  async function confirmInstallments(): Promise<void> {
    if (preview === null) return;
    const ifMatch =
      preview.action === 'installments.create' ? undefined : (installmentGroup?.revision ?? undefined);
    const previewId = preview.id;
    const { state } = await commit({
      operation: 'installments.commit',
      targetId: previewId,
      body: { previewId, confirm: true },
      send: (key) =>
        commitMutation(session.csrfToken, previewId, { idempotencyKey: key, ifMatch }),
    });
    if (state === 'saved') closeEditor();
  }

  async function deleteInstallments(): Promise<void> {
    if (editor?.entry == null) return;
    const target = editor.entry;
    const mapped = toInstallmentsDelete(target.installmentGroupId ?? '', [target.id]);
    if (!mapped.ok) {
      setSaveError(mapped.error);
      return;
    }
    setSaveError(null);
    await takePreview(mapped.body);
  }

  /* ----- payments (P6-T2) ----- */

  /** Open the panel on a new payment. */
  function openPaymentCreate(): void {
    resetSave();
    setEditor(null);
    setAccountEditorOpen(false);
    setPayment({ mode: 'create', transfer: null });
  }

  /** Open the panel on one stored pair, to complete it on a chosen date or to cancel it. */
  function openPaymentPair(transferId: string, mode: Exclude<PaymentMode, 'create'>): void {
    const target = transfers.find((candidate) => candidate.id === transferId);
    if (target === undefined) return;
    resetSave();
    setEditor(null);
    setAccountEditorOpen(false);
    setPayment({ mode, transfer: target });
  }

  /** The account the money arrives in, so the card position is read from the right impact. */
  function destinationOf(request: TransferMutationPreviewRequest, transfer: TransferBody | null): string | null {
    if (request.action === 'transfer.create' || request.action === 'transfer.fxCreate') {
      return request.destinationAccountId;
    }
    return transfer?.legs.find((leg) => leg.side === 'incoming')?.accountId ?? null;
  }

  /**
   * Store a preview of one payment change and reduce it to the figures the panel reviews.
   *
   * The preview moves no money, so its failure is a local error; the four save states belong to
   * the commit. `reuse` sends the *same* operation key as the last attempt, which is what makes
   * a reconciliation of a preview with an unknown outcome the same operation rather than a
   * second one.
   */
  async function takePaymentPreview(
    request: TransferMutationPreviewRequest,
    options: { readonly reuse?: boolean } = {},
  ): Promise<void> {
    const held = paymentIntentRef.current;
    const reuse = options.reuse === true && held !== null;
    const key = reuse && held !== null ? held.key : newOperationKey();
    paymentIntentRef.current = { request, key };
    setOperationKey({ key, reused: reuse });
    setPaymentPreviewError(null);
    setSaveError(null);
    setPaymentBusy(true);
    try {
      const stored = await createTransferPreview(session.csrfToken, request, { idempotencyKey: key });
      const prior = payment?.transfer ?? null;
      setPaymentPreview(
        paymentPreviewView(stored, prior, accounts, destinationOf(request, prior)),
      );
    } catch (error) {
      setPaymentPreview(null);
      if (error instanceof LedgerRequestError && endsSession(error.kind)) session.reportSessionLost();
      setPaymentPreviewError(
        error instanceof LedgerRequestError
          ? describeLedgerFailure(error.kind)
          : 'The payment preview could not be taken.',
      );
    } finally {
      setPaymentBusy(false);
    }
  }

  async function reviewPaymentCreate(form: PaymentForm): Promise<void> {
    if (payment === null) return;
    const mapped = toPaymentRequest(form, accounts);
    if (!mapped.ok) {
      setSaveError(mapped.error);
      return;
    }
    await takePaymentPreview(mapped.body);
  }

  async function reviewPaymentComplete(transferDate: string): Promise<void> {
    if (payment?.transfer == null) return;
    const mapped = toPaymentComplete(payment.transfer, transferDate);
    if (!mapped.ok) {
      setSaveError(mapped.error);
      return;
    }
    await takePaymentPreview(mapped.body);
  }

  async function reviewPaymentCancel(): Promise<void> {
    if (payment?.transfer == null) return;
    const mapped = toPaymentDelete(payment.transfer);
    if (!mapped.ok) {
      setSaveError(mapped.error);
      return;
    }
    await takePaymentPreview(mapped.body);
  }

  /** Commit the stored payment preview: the write that carries the four save states. */
  async function confirmPayment(): Promise<void> {
    if (paymentPreview === null) return;
    const previewId = paymentPreview.id;
    const ifMatch =
      paymentPreview.action === 'transfer.create' ? undefined : (paymentPreview.revision ?? undefined);
    const { state } = await commit({
      operation: 'payment.commit',
      targetId: previewId,
      body: { previewId, confirm: true },
      send: (key) =>
        commitTransferMutation(session.csrfToken, previewId, { idempotencyKey: key, ifMatch }),
    });
    if (state === 'saved') resetPayments();
  }

  /**
   * Reconcile an unknown outcome of the same intent.
   *
   * A stored preview is committed again with the retained key; a preview whose own outcome is
   * unknown is asked for again with the key it was asked for the first time, so the API replays
   * what it already stored instead of storing a second one.
   */
  async function reconcilePayment(): Promise<void> {
    if (paymentPreview !== null) {
      await confirmPayment();
      return;
    }
    const held = paymentIntentRef.current;
    if (held === null) return;
    await takePaymentPreview(held.request, { reuse: true });
  }

  async function submitEntry(form: EntryForm): Promise<void> {
    if (editor === null) return;
    if (editor.entry !== null) {
      const target = editor.entry;

      // A member of a group is edited through the group: the edit carries only the fields that
      // changed, for the scope the user chose, and no date or paid state at all (`INST-05`).
      if (target.installmentGroupId !== null) {
        const fields = editFieldsFrom(form, target);
        if (!fields.ok) {
          setSaveError(fields.error);
          return;
        }
        const mapped = toInstallmentsEdit(target.installmentGroupId, target.id, installmentScope, fields.body);
        if (!mapped.ok) {
          setSaveError(mapped.error);
          return;
        }
        setSaveError(null);
        await takePreview(mapped.body);
        return;
      }

      const mapped = toEntryPatch(form, target, accountRole);
      if (!mapped.ok) {
        setSaveError(mapped.error);
        return;
      }
      const { state } = await commit({
        operation: 'entry.update',
        targetId: target.id,
        body: mapped.body,
        send: (key) =>
          updateEntry(session.csrfToken, target.id, mapped.body, {
            idempotencyKey: key,
            ifMatch: target.revision,
          }),
      });
      if (state === 'saved') closeEditor();
      return;
    }

    // A create of more than one installment is generated through the group path; a single one is
    // an ordinary entry and takes the ordinary route.
    const count = parseInstallmentCount(installmentCount);
    if (!count.ok) {
      setSaveError(count.error);
      return;
    }
    if (count.body > 1) {
      const mapped = toInstallmentsCreate(form, count.body);
      if (!mapped.ok) {
        setSaveError(mapped.error);
        return;
      }
      setSaveError(null);
      await takePreview(mapped.body);
      return;
    }

    const mapped = toEntryCreate(form, accountRole);
    if (!mapped.ok) {
      setSaveError(mapped.error);
      return;
    }
    const { state } = await commit({
      operation: 'entry.create',
      targetId: null,
      body: mapped.body,
      send: (key) => createEntry(session.csrfToken, mapped.body, { idempotencyKey: key }),
    });
    if (state === 'saved') closeEditor();
  }

  async function payEntry(paymentDate: string): Promise<void> {
    if (editor?.entry == null) return;
    const target = editor.entry;
    const mapped = toPayPatch(target, paymentDate);
    if (!mapped.ok) {
      setSaveError(mapped.error);
      return;
    }
    const { state } = await commit({
      operation: 'entry.pay',
      targetId: target.id,
      body: mapped.body,
      send: (key) =>
        updateEntry(session.csrfToken, target.id, mapped.body, {
          idempotencyKey: key,
          ifMatch: target.revision,
        }),
    });
    if (state === 'saved') closeEditor();
  }

  async function submitAccount(form: AccountForm): Promise<void> {
    const mapped = toAccountCreate(form);
    if (!mapped.ok) {
      setSaveError(mapped.error);
      return;
    }
    const { state, value } = await commit({
      operation: 'account.create',
      targetId: null,
      body: mapped.body,
      send: (key) => createAccount(session.csrfToken, mapped.body, { idempotencyKey: key }),
    });
    if (state === 'saved') {
      const created = value as AccountBody | null;
      setAccountEditorOpen(false);
      if (created !== null) navigate({ account: created.id, page: 1 });
    }
  }

  /* ----- categories (P7-T1) ----- */

  /**
   * Create a category.
   *
   * A category is a label, so this is a metadata write rather than a money one — but it goes
   * through the same `commit`, because the four save states are how this application reports
   * *what is known* about a write, and a label is no different from an amount there.
   */
  async function submitCategory(name: string): Promise<void> {
    const mapped = toCategoryCreate(name);
    if (!mapped.ok) {
      setSaveError(mapped.error);
      return;
    }
    setSaveError(null);
    await commit({
      operation: 'category.create',
      targetId: null,
      body: mapped.body,
      send: (key) => createCategory(session.csrfToken, mapped.body, { idempotencyKey: key }),
    });
  }

  async function renameCategory(target: CategoryTarget, name: string): Promise<void> {
    const mapped = toCategoryRename(name);
    if (!mapped.ok) {
      setSaveError(mapped.error);
      return;
    }
    setSaveError(null);
    await commit({
      operation: 'category.rename',
      targetId: target.id,
      body: mapped.body,
      send: (key) =>
        updateCategory(session.csrfToken, target.id, mapped.body, {
          idempotencyKey: key,
          ifMatch: target.revision,
        }),
    });
  }

  async function archiveCategory(target: CategoryTarget, archived: boolean): Promise<void> {
    const body = toCategoryArchive(archived);
    setSaveError(null);
    await commit({
      operation: archived ? 'category.archive' : 'category.unarchive',
      targetId: target.id,
      body,
      send: (key) =>
        updateCategory(session.csrfToken, target.id, body, {
          idempotencyKey: key,
          ifMatch: target.revision,
        }),
    });
  }

  /**
   * The reference-safe deletion, and the one place the API's refusal is *quoted* rather than
   * summarised: the `409` names the reference that still carries the category, and that name is
   * what tells the user archiving is the alternative rather than a second attempt.
   */
  async function removeCategory(target: CategoryTarget): Promise<void> {
    setSaveError(null);
    await commit({
      operation: 'category.delete',
      targetId: target.id,
      body: { id: target.id, revision: target.revision },
      send: (key) =>
        deleteCategory(session.csrfToken, target.id, target.revision, { idempotencyKey: key }),
      refusal: (error) => describeReferenceRefusal(error, { what: 'category', name: target.name }),
    });
  }

  /* ----- accounts' other half (P7-T1): the reversible hide, and the same reference-safe delete ----- */

  async function setAccountArchived(candidate: AccountBody, archived: boolean): Promise<void> {
    setSaveError(null);
    await commit({
      operation: archived ? 'account.archive' : 'account.unarchive',
      targetId: candidate.id,
      body: { archived },
      send: (key) =>
        patchAccount(session.csrfToken, candidate.id, { archived }, {
          idempotencyKey: key,
          ifMatch: candidate.revision,
        }),
    });
  }

  async function removeAccount(candidate: AccountBody): Promise<void> {
    setSaveError(null);
    await commit({
      operation: 'account.delete',
      targetId: candidate.id,
      body: { id: candidate.id, revision: candidate.revision },
      send: (key) =>
        deleteAccount(session.csrfToken, candidate.id, candidate.revision, { idempotencyKey: key }),
      refusal: (error) => describeReferenceRefusal(error, { what: 'account', name: candidate.name }),
    });
  }

  /* ----- the entry lifecycle (P7-T1) ----- */

  /**
   * Store the impact of one `entry.delete` / `entry.duplicate` and reduce it to what is reviewed.
   *
   * Nothing is decided here: the preview is where the API computes the balance movement and the
   * eligibility, and the acknowledgement the commit will need. Taking a preview moves nothing, so
   * its failure is a local one, reported beside the editor rather than in the save states.
   */
  async function reviewLifecycle(action: 'delete' | 'duplicate'): Promise<void> {
    const target = editor?.entry ?? null;
    if (target === null) return;
    const mapped = action === 'delete' ? toEntryDelete(target) : toEntryDuplicate(target);
    if (!mapped.ok) {
      setLifecycle({ preview: null, error: mapped.error, busy: false, acknowledged: false });
      return;
    }
    setSaveError(null);
    setLifecycle({ preview: null, error: null, busy: true, acknowledged: false });
    try {
      const stored = await createEntryLifecyclePreview(session.csrfToken, mapped.body, {
        idempotencyKey: newOperationKey(),
      });
      setLifecycle({ preview: lifecyclePreviewView(stored), error: null, busy: false, acknowledged: false });
    } catch (error) {
      if (error instanceof LedgerRequestError && endsSession(error.kind)) session.reportSessionLost();
      setLifecycle({
        preview: null,
        error:
          error instanceof LedgerRequestError
            ? describeLedgerFailure(error.kind)
            : 'The impact of that change could not be read.',
        busy: false,
        acknowledged: false,
      });
    }
  }

  /**
   * Commit the stored lifecycle preview.
   *
   * `impactAcknowledged` comes from the user's own tick and only when the API said the impact needs
   * one, so an acknowledgement is never sent speculatively. The entry's revision is the `If-Match`,
   * which is what makes a commit of a row that moved also a refusal rather than a guess.
   */
  async function confirmLifecycle(): Promise<void> {
    const preview = lifecycle.preview;
    const target = editor?.entry ?? null;
    if (preview === null || target === null) return;
    const acknowledged = preview.impact.requiresConfirmation && lifecycle.acknowledged;
    const { state } = await commit({
      operation: `${preview.action}.commit`,
      targetId: preview.id,
      body: { previewId: preview.id, confirm: true, impactAcknowledged: acknowledged },
      send: (key) =>
        commitEntryLifecycle(session.csrfToken, preview.id, acknowledged, {
          idempotencyKey: key,
          ifMatch: target.revision,
        }),
    });
    if (state === 'saved') closeEditor();
  }

  /**
   * The reversible cancellation.
   *
   * Two routes rather than one, so the button calls the one that says what it does: a canceled row
   * is restored and a live one is cancelled, and neither removes anything — the row keeps its
   * identity, so this cannot be a destructive control.
   */
  async function toggleCancellation(): Promise<void> {
    const target = editor?.entry ?? null;
    if (target === null) return;
    const cancel = !target.canceled;
    const { state } = await commit({
      operation: cancel ? 'entry.cancel' : 'entry.restore',
      targetId: target.id,
      body: { cancel },
      send: (key) =>
        setEntryCancellation(session.csrfToken, target.id, cancel, {
          idempotencyKey: key,
          ifMatch: target.revision,
        }),
    });
    if (state === 'saved') closeEditor();
  }

  /* ----------------------------------------------------------------- rendering --- */

  const filtered = hasRowFilters(context);
  const currency = account?.currency ?? '';
  /**
   * The accounts shown, archived ones behind the toggle.
   *
   * The per-currency cash/card figures above still include an archived account: hiding a row is a
   * view decision, while its balance is money that exists (`P7-T1`: archiving is visibility, never a
   * money change).
   */
  const shownAccounts = useMemo(
    () => accounts.filter((candidate) => showArchivedAccounts || !candidate.archived),
    [accounts, showArchivedAccounts],
  );
  const paymentList = useMemo(
    () => (account === null ? [] : paymentRows(transfers, account.id, accounts)),
    [transfers, account, accounts],
  );

  return (
    <section className="workspace" aria-label={screen === 'accounts' ? 'Accounts' : 'Monthly ledger'}>
      <section className="panel" aria-labelledby="directory-heading">
        <h2 id="directory-heading">Accounts</h2>
        <div className="actions">
          <button
            type="button"
            data-testid="new-account"
            onClick={() => {
              resetSave();
              setEditor(null);
              setAccountEditorOpen(true);
            }}
          >
            New account
          </button>
        </div>

        <div data-testid="directory">
          {groups.map((group) => (
            <div className="currency-group" key={group.currency} data-currency={group.currency}>
              <h3>{group.currency}</h3>
              <dl className="status-grid">
                <dt>Cash</dt>
                <dd className="money" data-testid={`cash-total-${group.currency}`}>
                  {formatBalance(group.cashMinor, group.currency)}
                </dd>
                <dt>Card</dt>
                <dd className="money" data-testid={`card-total-${group.currency}`}>
                  {formatBalance(group.cardMinor, group.currency)}
                </dd>
              </dl>
            </div>
          ))}
        </div>

        <ul className="directory-list">
          {shownAccounts.map((candidate) => (
            <li key={candidate.id} data-archived={candidate.archived}>
              <button
                type="button"
                className="account-button"
                data-testid="account-item"
                data-account-id={candidate.id}
                aria-current={candidate.id === accountId}
                onClick={() => navigate({ account: candidate.id, page: 1 })}
              >
                <span>
                  {candidate.name}{' '}
                  <span className="muted">
                    · {candidate.kind}
                    {candidate.archived ? ' · archived' : ''}
                    {candidate.kind === 'card' ? (
                      <>
                        {' · '}
                        <span data-testid="account-state">
                          {cardState(BigInt(candidate.balance.minorUnits))}
                        </span>
                      </>
                    ) : null}
                  </span>
                </span>
                <span
                  className={`money${BigInt(candidate.balance.minorUnits) < 0n ? ' negative' : ''}`}
                  data-testid="account-balance"
                >
                  {formatBalance(BigInt(candidate.balance.minorUnits), candidate.currency)}
                </span>
              </button>
              <span className="actions">
                <button
                  type="button"
                  data-testid="account-archive"
                  disabled={saveState === 'saving'}
                  onClick={() => {
                    void setAccountArchived(candidate, !candidate.archived);
                  }}
                >
                  {candidate.archived ? 'Unhide' : 'Hide'}
                </button>
                <button
                  type="button"
                  data-testid="account-delete"
                  disabled={saveState === 'saving'}
                  onClick={() => {
                    void removeAccount(candidate);
                  }}
                >
                  Delete
                </button>
              </span>
            </li>
          ))}
        </ul>

        <label className="field">
          <input
            type="checkbox"
            data-testid="account-archived-toggle"
            checked={showArchivedAccounts}
            onChange={(event) => setShowArchivedAccounts(event.target.checked)}
          />
          Show hidden accounts (their balances are still counted above)
        </label>

        {accountsQuery.isPending ? <p className="hint">Loading accounts…</p> : null}
        {accountsQuery.isError ? (
          <p className="notice bad" data-testid="accounts-error">
            {failureText(accountsQuery.error)}
          </p>
        ) : null}
        {accountsQuery.isSuccess && accounts.length === 0 ? (
          <p data-testid="no-accounts">No accounts yet — create one to begin.</p>
        ) : null}
      </section>

      {/*
        * The second column. The accounts screen shows the categories panel where the ledger screen
        * shows the month: the categories are a setup surface, and the operator's decision of
        * 2026-10-18 puts them on the Accounts screen with the rest of them.
        *
        * The ledger's own panel below is left exactly as it was — its markup, its test ids and its
        * behaviour are unchanged, and the branch is only about which screen reaches it.
        */}
      {screen === 'accounts' ? (
        <CategoryPanel
          categories={categoriesQuery.data?.categories ?? []}
          showArchived={showArchivedCategories}
          saveState={saveState}
          saveMessage={saveMessage}
          localError={saveError}
          onToggleArchived={setShowArchivedCategories}
          onCreate={(name) => {
            void submitCategory(name);
          }}
          onRename={(target, name) => {
            void renameCategory(target, name);
          }}
          onArchive={(target, archived) => {
            void archiveCategory(target, archived);
          }}
          onDelete={(target) => {
            void removeCategory(target);
          }}
        />
      ) : (
      <section className="panel" aria-labelledby="ledger-heading" data-testid="ledger">
        <h2 id="ledger-heading">Monthly ledger</h2>

        <div className="toolbar">
          <div className="field">
            <span>Month</span>
            <div className="month">
              <button
                type="button"
                data-testid="prev-month"
                aria-label="Previous month"
                onClick={() => navigate({ month: shiftMonth(context.month, -1), page: 1 })}
              >
                ‹
              </button>
              <input
                type="month"
                data-testid="month-input"
                value={context.month}
                onChange={(event) => navigate({ month: event.target.value || context.month, page: 1 })}
              />
              <button
                type="button"
                data-testid="next-month"
                aria-label="Next month"
                onClick={() => navigate({ month: shiftMonth(context.month, 1), page: 1 })}
              >
                ›
              </button>
            </div>
            <span data-testid="month-label">{monthLabel(context.month)}</span>
          </div>

          <label className="field">
            Rows
            <select
              data-testid="row-mode"
              value={context.mode}
              onChange={(event) => navigate({ mode: event.target.value as LedgerContext['mode'], page: 1 })}
            >
              <option value="due">Due date in month</option>
              <option value="transaction">Transaction date in month</option>
            </select>
          </label>

          <label className="field">
            Search
            <input
              type="search"
              data-testid="row-search"
              value={context.query}
              onChange={(event) => navigate({ query: event.target.value, page: 1 })}
            />
          </label>

          <label className="field">
            Sort
            <select
              data-testid="row-sort"
              value={context.sort}
              onChange={(event) => navigate({ sort: event.target.value as LedgerContext['sort'], page: 1 })}
            >
              <option value="date-asc">Date, oldest first</option>
              <option value="date-desc">Date, newest first</option>
              <option value="amount-desc">Amount, largest first</option>
              <option value="amount-asc">Amount, smallest first</option>
            </select>
          </label>

          <label className="field">
            Type
            <select
              data-testid="type-filter"
              value={context.type}
              onChange={(event) => navigate({ type: event.target.value as LedgerContext['type'], page: 1 })}
            >
              <option value="all">All types</option>
              <option value="income">Income</option>
              <option value="expense">Expense</option>
              <option value="opening">Opening</option>
              <option value="transfer">Transfer</option>
            </select>
          </label>

          <label className="field">
            State
            <select
              data-testid="state-filter"
              value={context.state}
              onChange={(event) => navigate({ state: event.target.value as LedgerContext['state'], page: 1 })}
            >
              <option value="all">Any state</option>
              <option value="effective">Effective</option>
              <option value="pending">Pending</option>
            </select>
          </label>

          <div className="field">
            <span>Filters</span>
            <button
              type="button"
              data-testid="clear-filters"
              onClick={() => navigate({ query: '', type: 'all', state: 'all', page: 1 })}
            >
              Clear filters
            </button>
          </div>

          {/*
            Canceled rows are a scope the *server* decides, so this one is context (the address)
            rather than a component's private preference: it is what makes a canceled row — and
            therefore its restoration — reachable at all (`P7-T1`).
          */}
          <label className="field">
            <input
              type="checkbox"
              data-testid="show-canceled"
              checked={context.canceled}
              onChange={(event) => navigate({ canceled: event.target.checked, page: 1 })}
            />
            Show canceled rows (they contribute nothing)
          </label>
        </div>

        {businessToday !== null ? (
          <p className="hint">
            Server business day{' '}
            <strong data-testid="business-today">{businessToday}</strong> · {BUSINESS_TIME_ZONE}
          </p>
        ) : null}

        {account === null ? (
          <p data-testid="no-account">Choose an account to see its ledger.</p>
        ) : (
          <>
            {summary !== null ? (
              <>
                <dl className="summary-grid" data-testid="summary">
                  <div>
                    <dt className="summary-label">Opening before {bounds.start}</dt>
                    <dd className="money" data-testid="opening-before">
                      {summary.openingMinor === null
                        ? <span className="unknown">unknown</span>
                        : formatBalance(summary.openingMinor, summary.currency)}
                    </dd>
                  </div>
                  <div>
                    <dt className="summary-label">Recorded today · full history</dt>
                    <dd
                      className={`money${summary.currentNegative ? ' negative' : ''}`}
                      data-testid="current-balance"
                      data-negative={summary.currentNegative ? 'true' : 'false'}
                    >
                      {formatBalance(summary.currentMinor, summary.currency)}
                    </dd>
                  </div>
                  <div>
                    <dt className="summary-label">Month-end recorded</dt>
                    <dd className="money" data-testid="month-end">
                      {summary.monthEndMinor === null
                        ? <span className="unknown">unknown</span>
                        : formatBalance(summary.monthEndMinor, summary.currency)}
                    </dd>
                  </div>
                </dl>

                {account.kind === 'card' ? (
                  <p
                    className="hint"
                    data-testid="card-state"
                    data-card-state={cardState(summary.currentMinor)}
                  >
                    {CARD_STATE_TEXT[cardState(summary.currentMinor)]}
                  </p>
                ) : null}

                {summary.unknownReason !== null ? (
                  <p className="unknown" data-testid="unknown-history">
                    {summary.unknownReason}
                  </p>
                ) : null}
              </>
            ) : null}

            {activity !== null ? (
              <div className="subtotals">
                <span data-testid="activity-label">
                  {filtered ? 'Matching activity · filtered' : 'Period activity'}
                </span>
                <span>
                  Income{' '}
                  <b className="money" data-testid="activity-income">
                    {formatMoneyMagnitude(activity.incomeMinor, currency)}
                  </b>
                </span>
                <span>
                  Expenses{' '}
                  <b className="money" data-testid="activity-expense">
                    {formatMoneyMagnitude(activity.expenseMinor, currency)}
                  </b>
                </span>
                <span>
                  Net{' '}
                  <b
                    className={`money${activity.netMinor < 0n ? ' negative' : ''}`}
                    data-testid="activity-net"
                  >
                    {formatBalance(activity.netMinor, currency)}
                  </b>
                </span>
              </div>
            ) : null}

            <div className="actions">
              <button type="button" className="primary" data-testid="new-entry" onClick={() => openEntryEditor(null)}>
                New entry
              </button>
              <button type="button" data-testid="new-payment" onClick={openPaymentCreate}>
                New payment
              </button>
            </div>

            {/*
              The account's payments: independent pairs, each one whole. A payment is not an
              allocation against a purchase, so a card can be paid several times, early, or by
              more than it owes — in which case the card is left in credit rather than a debt
              clamped at zero.
            */}
            <section className="payments" data-testid="payments" aria-label="Payments">
              <h3>Payments</h3>
              <p className="hint">
                Money leaving a cash account and arriving in another account, as one pair. A card
                can be paid in several partial payments, early or late — each is its own pair.
              </p>

              {transfersQuery.isPending ? <p className="hint">Loading payments…</p> : null}
              {transfersQuery.isError ? (
                <p className="notice bad" data-testid="payments-error">
                  {failureText(transfersQuery.error)}
                </p>
              ) : null}

              <ul className="directory-list" data-testid="payment-rows">
                {paymentList.map((row) => (
                  <li key={row.id}>
                    <div
                      className="payment-row"
                      data-testid="payment-row"
                      data-transfer-id={row.id}
                      data-status={row.canceled ? 'canceled' : row.status}
                      data-planned={row.planned ? 'true' : 'false'}
                    >
                      <span className="description" data-testid="payment-row-description">
                        {row.description}
                      </span>
                      <span className="meta">
                        <span data-testid="payment-row-counterpart">{row.counterpartName ?? 'unknown'}</span>
                        <span data-testid="payment-row-date">{row.transferDate}</span>
                        <span data-testid="payment-row-state">
                          {row.canceled ? 'canceled' : row.status}
                        </span>
                      </span>
                      <span
                        className={`money${row.legMinor < 0n ? ' negative' : ''}`}
                        data-testid="payment-row-amount"
                      >
                        {formatMoney(row.legMinor, row.currency)}
                      </span>
                      {row.cancelable ? (
                        <span className="row-actions">
                          {row.planned ? (
                            <button
                              type="button"
                              data-testid="payment-complete-open"
                              onClick={() => openPaymentPair(row.id, 'complete')}
                            >
                              Complete on a date
                            </button>
                          ) : null}
                          <button
                            type="button"
                            data-testid="payment-cancel-open"
                            onClick={() => openPaymentPair(row.id, 'cancel')}
                          >
                            Cancel payment
                          </button>
                        </span>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>

              {transfersQuery.isSuccess && paymentList.length === 0 ? (
                <p data-testid="payments-empty">No payments on this account yet.</p>
              ) : null}
            </section>

            {entriesQuery.isPending ? <p className="hint">Loading entries…</p> : null}
            {entriesQuery.isError ? (
              <p className="notice bad" data-testid="entries-error">
                {failureText(entriesQuery.error)}
              </p>
            ) : null}

            <BulkActions
              selection={bulkSelection}
              categories={categoriesQuery.data?.categories ?? []}
              businessToday={businessToday ?? fallbackMonth}
              state={bulk}
              saveState={saveState}
              saveMessage={saveMessage}
              onClear={clearBulk}
              onPreview={(action, options) => {
                void takeBulkPreview(action, options);
              }}
              onCommit={() => {
                void confirmBulk();
              }}
              onDismiss={() => setBulk({ action: null, preview: null, busy: false, error: null })}
            />

            <ul className="ledger-list" data-testid="ledger-rows">
              {(selection?.pageRows ?? []).map((row) => {
                const minor = BigInt(row.entry.signedMinor);
                return (
                  <li key={row.entry.id} data-selected={isSelected(bulkSelection, row.entry.id) ? 'true' : 'false'}>
                    <label className="bulk-check">
                      <input
                        type="checkbox"
                        data-testid="row-select"
                        data-entry-id={row.entry.id}
                        checked={isSelected(bulkSelection, row.entry.id)}
                        onChange={() => toggleBulkRow(row.entry.id)}
                        aria-label={`Select ${row.entry.description}`}
                      />
                    </label>
                    <button
                      type="button"
                      className="ledger-row"
                      data-testid="ledger-row"
                      data-entry-id={row.entry.id}
                      data-eligible={row.eligible ? 'true' : 'false'}
                      data-canceled={row.entry.canceled ? 'true' : 'false'}
                      onClick={() => openEntryEditor(row.entry)}
                    >
                      <span className="description" data-testid="row-description">
                        {row.entry.description}
                      </span>
                      <span
                        className={`money${minor < 0n ? ' negative' : ''}`}
                        data-testid="row-amount"
                        data-negative={minor < 0n ? 'true' : 'false'}
                      >
                        {formatMoney(minor, row.entry.currency)}
                      </span>
                      <span className="meta">
                        <span data-testid="row-date">{row.date}</span>
                        <span data-testid="row-state">{row.stateLabel}</span>
                        <span data-testid="row-type">{row.entry.type}</span>
                        {row.entry.installmentOrdinal !== null ? (
                          <span data-testid="row-installment" data-ordinal={row.entry.installmentOrdinal}>
                            Installment {row.entry.installmentOrdinal}
                          </span>
                        ) : null}
                      </span>
                      <span className="running" data-testid="row-running">
                        Running after this row: {formatBalance(row.runningMinor, row.entry.currency)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            {selection !== null && selection.total === 0 ? (
              <p data-testid="rows-empty">No rows match this view.</p>
            ) : null}

            {selection !== null ? (
              <div className="pager">
                <button
                  type="button"
                  data-testid="prev-page"
                  disabled={selection.page <= 1}
                  onClick={() => navigate({ page: selection.page - 1 })}
                >
                  Previous
                </button>
                <span data-testid="page-label">
                  Page {selection.page} of {selection.pageCount} · {selection.total} rows
                </span>
                <button
                  type="button"
                  data-testid="next-page"
                  disabled={selection.page >= selection.pageCount}
                  onClick={() => navigate({ page: selection.page + 1 })}
                >
                  Next
                </button>
              </div>
            ) : null}
          </>
        )}
      </section>
      )}

      {editor !== null ? (
        <EntryEditor
          entry={editor.entry}
          accounts={accounts}
          defaultAccountId={accountId ?? ''}
          businessToday={businessToday ?? fallbackMonth}
          saveState={saveState}
          saveMessage={saveMessage}
          localError={saveError}
          operationKey={operationKey}
          installmentCount={installmentCount}
          onChangeInstallmentCount={setInstallmentCount}
          installmentGroup={installmentGroup}
          installmentScope={installmentScope}
          onChangeInstallmentScope={setInstallmentScope}
          installmentPreview={preview}
          installmentPreviewError={previewError}
          installmentBusy={installmentBusy}
          onConfirmInstallments={() => {
            void confirmInstallments();
          }}
          onDeleteInstallments={() => {
            void deleteInstallments();
          }}
          onReconcileInstallments={() => {
            void confirmInstallments();
          }}
          onSubmit={(form) => {
            void submitEntry(form);
          }}
          onPay={payEntry}
          categories={categoriesQuery.data?.categories ?? []}
          suggestion={{
            query: suggestionQuery,
            items: suggestionsQuery.data?.suggestions ?? [],
            busy: suggestionsQuery.isFetching,
            error: suggestionsQuery.isError ? failureText(suggestionsQuery.error) : null,
          }}
          onSuggestionQuery={setSuggestionQuery}
          lifecycle={lifecycle}
          onReviewLifecycle={(action) => {
            void reviewLifecycle(action);
          }}
          onAcknowledgeLifecycle={(value) => {
            setLifecycle((current) => ({ ...current, acknowledged: value }));
          }}
          onConfirmLifecycle={() => {
            void confirmLifecycle();
          }}
          onToggleCancellation={() => {
            void toggleCancellation();
          }}
          onCancel={closeEditor}
        />
      ) : null}

      {payment !== null ? (
        <PaymentPanel
          accounts={accounts}
          defaultSourceAccountId={account?.kind === 'cash' ? (accountId ?? '') : ''}
          defaultDestinationAccountId={account?.kind === 'card' ? (accountId ?? '') : ''}
          businessToday={businessToday ?? fallbackMonth}
          transfer={payment.transfer}
          mode={payment.mode}
          saveState={saveState}
          saveMessage={saveMessage}
          localError={saveError}
          operationKey={operationKey}
          preview={paymentPreview}
          previewError={paymentPreviewError}
          busy={paymentBusy}
          suggestions={paymentSuggestions}
          onReviewCreate={(form) => {
            void reviewPaymentCreate(form);
          }}
          onReviewComplete={(date) => {
            void reviewPaymentComplete(date);
          }}
          onReviewCancel={() => {
            void reviewPaymentCancel();
          }}
          onConfirm={() => {
            void confirmPayment();
          }}
          onReconcile={() => {
            void reconcilePayment();
          }}
          onClose={resetPayments}
        />
      ) : null}

      {accountEditorOpen ? (
        <AccountEditor
          businessToday={businessToday ?? `${fallbackMonth}-01`}
          saveState={saveState}
          saveMessage={saveMessage}
          localError={saveError}
          onSubmit={submitAccount}
          onCancel={() => {
            resetSave();
            setAccountEditorOpen(false);
          }}
        />
      ) : null}
    </section>
  );
}

/** A read failure, in the same words the editor uses for a write failure. */
function failureText(error: unknown): string {
  if (error instanceof LedgerRequestError) return describeLedgerFailure(error.kind);
  return 'The API could not be reached.';
}

/**
 * Reconciliation of an unknown outcome is the editor's retry, which is why this component
 * exports nothing else: the state machine is `save.ts` and the wording is `describeSaveState`.
 */
export { WRITE_MESSAGES };
